import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Mic,
  Video,
  CheckCircle,
  Lock,
  Play,
  ArrowRight,
  ArrowLeft,
  BookOpen,
  Target,
  Wrench,
  FileText,
  Megaphone,
  DollarSign,
  Award,
  Headphones,
  Download,
  Loader2,
  Home,
  Lightbulb
} from "lucide-react";

const WEEK_ICONS = {
  1: Target,
  2: Wrench,
  3: FileText,
  4: Headphones,
  5: Megaphone,
  6: DollarSign
};

export function PodcastDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [enrollment, setEnrollment] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    fetchEnrollment();
  }, []);

  const fetchEnrollment = async () => {
    try {
      const [enrollRes, currRes] = await Promise.all([
        authFetch(`${API}/podcast/enrollment`),
        authFetch(`${API}/podcast/curriculum`)
      ]);

      if (enrollRes.ok) {
        const data = await enrollRes.json();
        setEnrollment(data);
      }

      if (currRes.ok) {
        const data = await currRes.json();
        setCurriculum(data);
      }
    } catch (_error) {
      console.error("Error fetching enrollment:", _error);
      toast.error("Failed to load program data");
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const response = await authFetch(`${API}/podcast/enroll`, {
        method: "POST",
        headers: { "Content-Type": "application/json" }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Enrolled successfully!");
        navigate("/podcast/intake");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to enroll");
      }
    } catch (_error) {
      toast.error("Failed to enroll");
    } finally {
      setEnrolling(false);
    }
  };

  const downloadCertificate = async () => {
    try {
      const response = await authFetch(`${API}/podcast/certificate/${user.user_id}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Podcast_Academy_Certificate_${user.name?.replace(/\s/g, "_")}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Certificate downloaded!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to download certificate");
      }
    } catch (_error) {
      toast.error("Failed to download certificate");
    }
  };

  const getProgress = () => {
    if (!enrollment) return 0;
    const completedWeeks = enrollment.completed_weeks?.length || 0;
    return Math.round((completedWeeks / 6) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  // Not enrolled - show enrollment page
  if (!enrollment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50">
        {/* Header */}
        <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-neutral-900">Podcast Launch Academy</h1>
                  <p className="text-xs text-neutral-500">6-Week Program</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-12">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Mic className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">
              Launch Your Podcast in 6 Weeks
            </h1>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              A comprehensive program designed to take you from idea to launch, with AI-powered tools to accelerate your success.
            </p>
          </div>

          {/* What You'll Learn */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-purple-500" />
                What You'll Learn
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { week: 1, title: "Foundation & Discovery", desc: "Find your niche and target audience" },
                  { week: 2, title: "Equipment & Setup", desc: "Build your recording environment" },
                  { week: 3, title: "Content Planning", desc: "Create compelling episode ideas" },
                  { week: 4, title: "Recording & Production", desc: "Professional recording techniques" },
                  { week: 5, title: "Marketing & Promotion", desc: "Grow your audience from day one" },
                  { week: 6, title: "Monetization & Growth", desc: "Turn your podcast into a business" }
                ].map((item) => {
                  const Icon = WEEK_ICONS[item.week];
                  return (
                    <div key={item.week} className="flex items-start gap-3 p-4 bg-neutral-50 rounded-lg">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-neutral-900">Week {item.week}: {item.title}</p>
                        <p className="text-sm text-neutral-600">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* AI Tools */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                AI-Powered Tools Included
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  "Niche Finder Generator",
                  "Episode Title Generator",
                  "Show Notes Generator",
                  "Marketing Plan Generator",
                  "One Sheet / Media Kit",
                  "Monetization Strategy"
                ].map((tool) => (
                  <div key={tool} className="flex items-center gap-2 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-neutral-700">{tool}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Choose Format */}
          <Card className="mb-8 border-purple-200">
            <CardHeader className="text-center">
              <CardTitle>Choose Your Podcast Format</CardTitle>
              <CardDescription>
                The curriculum adapts based on whether you're creating an audio or video podcast
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <div className="flex-1 p-6 border-2 border-neutral-200 rounded-xl text-center hover:border-purple-300 transition-colors">
                  <Headphones className="w-12 h-12 text-purple-500 mx-auto mb-3" />
                  <h3 className="font-bold text-lg mb-2">Audio Podcast</h3>
                  <p className="text-sm text-neutral-600">
                    Focus on audio recording, editing, and distribution
                  </p>
                </div>
                <div className="flex-1 p-6 border-2 border-neutral-200 rounded-xl text-center hover:border-pink-300 transition-colors">
                  <Video className="w-12 h-12 text-pink-500 mx-auto mb-3" />
                  <h3 className="font-bold text-lg mb-2">Video Podcast</h3>
                  <p className="text-sm text-neutral-600">
                    Audio + video recording, editing, and YouTube optimization
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Button
              size="lg"
              onClick={handleEnroll}
              disabled={enrolling}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8"
              data-testid="enroll-podcast-btn"
            >
              {enrolling ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Enrolling...
                </>
              ) : (
                <>
                  Start Your Podcast Journey
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
            <p className="text-sm text-neutral-500 mt-3">
              You'll start with a brief intake to personalize your experience
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Needs to complete intake
  if (enrollment.status === "intake_pending") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50">
        <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-neutral-900">Podcast Launch Academy</h1>
                  <p className="text-xs text-neutral-500">Complete Your Intake</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-4">Complete Your Intake Form</h2>
          <p className="text-neutral-600 mb-8">
            Before we begin, we need to learn about your podcast goals to personalize your experience.
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/podcast/intake")}
            className="bg-gradient-to-r from-purple-600 to-pink-600"
            data-testid="go-to-intake-btn"
          >
            Start Intake Form
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </main>
      </div>
    );
  }

  // Enrolled - show curriculum dashboard
  const progress = getProgress();
  const isGraduated = enrollment.status === "graduated";

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                <Mic className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-neutral-900">Podcast Launch Academy</h1>
                <p className="text-xs text-neutral-500">
                  {isGraduated ? "Graduate" : `Week ${enrollment.current_week} of 6`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isGraduated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadCertificate}
                  className="border-purple-200 text-purple-600"
                  data-testid="download-certificate-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Certificate
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Progress Overview */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-neutral-900">Your Progress</h2>
                <p className="text-neutral-600">
                  {isGraduated
                    ? "Congratulations! You've completed the program!"
                    : `${enrollment.completed_weeks?.length || 0} of 6 weeks completed`}
                </p>
              </div>
              {isGraduated && (
                <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <Award className="w-4 h-4 mr-1" />
                  Graduate
                </Badge>
              )}
            </div>
            <Progress value={progress} className="h-3" />
            <div className="flex justify-between mt-2 text-sm text-neutral-500">
              <span>Week 1</span>
              <span>Week 6</span>
            </div>
          </CardContent>
        </Card>

        {/* Podcast Info */}
        {enrollment.intake_data && (
          <Card className="mb-8 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center flex-shrink-0">
                  {enrollment.podcast_type === "video" ? (
                    <Video className="w-6 h-6 text-purple-600" />
                  ) : (
                    <Headphones className="w-6 h-6 text-purple-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-neutral-900">
                    {enrollment.intake_data.podcast_name || "Your Podcast"}
                  </h3>
                  <p className="text-sm text-neutral-600 mb-2">
                    {enrollment.podcast_type === "video" ? "Video Podcast" : "Audio Podcast"}
                  </p>
                  <p className="text-sm text-neutral-500">
                    Target: {enrollment.intake_data.target_audience}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Curriculum Grid */}
        <h3 className="text-lg font-bold text-neutral-900 mb-4">6-Week Curriculum</h3>
        <div className="grid gap-4">
          {curriculum && Object.entries(curriculum).map(([weekNum, week]) => {
            const num = parseInt(weekNum);
            const Icon = WEEK_ICONS[num];
            const isCompleted = week.completed;
            const isUnlocked = week.unlocked;
            const isCurrent = num === enrollment.current_week;

            return (
              <Card
                key={num}
                className={`transition-all ${
                  isCurrent
                    ? "border-purple-400 shadow-lg"
                    : isCompleted
                    ? "border-green-200 bg-green-50/50"
                    : isUnlocked
                    ? "border-neutral-200 hover:border-purple-300"
                    : "border-neutral-100 bg-neutral-50 opacity-75"
                }`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        isCompleted
                          ? "bg-green-100"
                          : isCurrent
                          ? "bg-gradient-to-br from-purple-500 to-pink-500"
                          : isUnlocked
                          ? "bg-purple-100"
                          : "bg-neutral-200"
                      }`}
                    >
                      {isCompleted ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : isUnlocked ? (
                        <Icon className={`w-6 h-6 ${isCurrent ? "text-white" : "text-purple-600"}`} />
                      ) : (
                        <Lock className="w-6 h-6 text-neutral-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-purple-600">Week {num}</span>
                        {isCurrent && (
                          <Badge variant="outline" className="border-purple-300 text-purple-600 text-xs">
                            Current
                          </Badge>
                        )}
                        {isCompleted && (
                          <Badge className="bg-green-100 text-green-700 text-xs">Completed</Badge>
                        )}
                      </div>
                      <h4 className="font-bold text-neutral-900">{week.title}</h4>
                      <p className="text-sm text-neutral-600">{week.subtitle}</p>
                      {week.ai_tool_name && (
                        <p className="text-xs text-purple-500 mt-1 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" />
                          AI Tool: {week.ai_tool_name}
                        </p>
                      )}
                    </div>
                    <div>
                      {isUnlocked ? (
                        <Button
                          variant={isCurrent ? "default" : "outline"}
                          size="sm"
                          onClick={() => navigate(`/podcast/week/${num}`)}
                          className={isCurrent ? "bg-gradient-to-r from-purple-600 to-pink-600" : ""}
                          data-testid={`week-${num}-btn`}
                        >
                          {isCompleted ? "Review" : "Start"}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" disabled>
                          <Lock className="w-4 h-4 mr-1" />
                          Locked
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}

export default PodcastDashboard;
